import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import {
  Dashboard,
  Filters,
  GetDashboardRequest,
} from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { DashboardServicePromiseClient } from '@trustero/trustero-api-web/lib/dashboard/dashboard_grpc_web_pb'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { ParsedQuery } from 'query-string'
import { useInAudit } from 'src/context/AuditContext'
import {
  GrpcResponse,
  NewGrpcResponse,
} from '../../components/async/hooks/types'
import { useSwrImmutableGrpc } from '../../components/async/useSwrImmutableGrpc'
import {
  applyDashboardFilters,
  getIsDashboardAuditSpecific,
} from './GrcDashboards.helpers'
import { getSections, Section } from './utils'
import { GrcDashboardQueryParam, GrcDashboardTab } from './GrcDashboards.types'

export const useGetDashboard = (
  dname: string,
  filters: Filters,
  shouldFetch = false,
): GrpcResponse<Dashboard> => {
  const hasName = !!(dname && dname.length)
  const { auditId } = useInAudit()
  const request = new GetDashboardRequest()
    .setName(dname || '')
    .setIncludeData(true)
    .setFilters(filters)
  if (getIsDashboardAuditSpecific(dname)) {
    request.setAuditId(auditId || '')
  }
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboard,
    request,
    shouldFetch && hasName,
  )

  return NewGrpcResponse(response)
}

export function useDashboardFilter(): Filters {
  const location = useLocation()
  const filters = useMemo(() => {
    const end = 0
    const start = Date.now() - 1000 * 60 * 60 * 24 * 31
    const filters = new Filters() // Apply all filters for each parameter in URL
    filters.setStarttime(start)
    filters.setEndtime(end)
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyDashboardFilters(filters, queryParams, filterType),
    )
    return filters
  }, [location.search])
  return filters
}

export const useDashboardFrameworkSections = (
  dashboard?: Dashboard,
): Section[] => {
  const location = useLocation()
  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const frameworkFilters = queryParams[
    FilterParam.DASHBOARD_FRAMEWORK
  ] as string[]

  return useMemo(() => {
    if (!dashboard) {
      return []
    }
    return getSections(dashboard, frameworkFilters)
  }, [dashboard, frameworkFilters])
}

export const useGetURLParams = (): ParsedQuery<string> => {
  const location = useLocation()
  return queryString.parse(location.search)
}

export const useGetURLParam = <T>(
  name: string,
  validOptions: Set<T>,
  defaultValue: T,
): T => {
  const params = useGetURLParams()
  if (!params[name]) {
    return defaultValue
  }
  const param = Array.isArray(params[name])
    ? (params[name] as Array<unknown>)[0]
    : params[name]
  if (validOptions.has(param as T)) {
    return param as T
  }
  return defaultValue
}

export const useActiveGrcDashboardTab = (): GrcDashboardTab => {
  const options = new Set(Object.values(GrcDashboardTab))
  return useGetURLParam(
    GrcDashboardQueryParam.ACTIVE_TAB,
    options,
    GrcDashboardTab.COMPLIANCE,
  )
}

export const useRecomputeGrcDashboardURLParams = (): void => {
  const location = useLocation()
  const navigate = useNavigate()
  const query = useGetURLParams()
  if (!query[GrcDashboardQueryParam.ACTIVE_TAB]) {
    // Recompute query
    if (!query[GrcDashboardQueryParam.ACTIVE_TAB]) {
      query[GrcDashboardQueryParam.ACTIVE_TAB] = GrcDashboardTab.COMPLIANCE
    }

    navigate(
      `${queryString.stringifyUrl({
        url: location.pathname,
        query,
      })}${location.hash}`,
    )
  }
  return
}

export const useIsCustomTimeRange = (): boolean => {
  const queryParams = useGetURLParams()
  return !!(
    queryParams[FilterParam.DASHBOARD_DATE_RANGE] &&
    queryParams[FilterParam.DASHBOARD_DATE_RANGE][0].split('-').length > 1
  )
}
